import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import cloneDeep from 'lodash/cloneDeep'
import axios from '~/data/http/axios'
import AiLoadingIcon from '~/Common-Objects/LoadingIcons/aiLoadingIcon'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import TextArea from '~/Common-Objects/UI/Forms/TextArea/TextArea'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import i18n from '~/i18n'
import DragDropFunctions from '~/utils/TemplateEditor/dragDropFunctions'
import PanelHeader from '~/Common-Objects/TemplateEditor/PanelHeader'
import './style.scss'

const SUGGESTION_KEYS = ['suggestion1', 'suggestion2', 'suggestion3']

const storageKey = (storageId, isLandingPage) =>
    `${isLandingPage ? 'ai-landing-refine' : 'ai-refine'}:${storageId || 'new'}`

const readPersistedState = (storageId, isLandingPage) => {
    try {
        const raw = sessionStorage.getItem(storageKey(storageId, isLandingPage))
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null
        // Older builds stored a client-chosen model. The API now always uses its
        // default, so a non-default session cannot be continued.
        const storedModel = typeof parsed.model === 'string' ? parsed.model : ''
        const sessionCompatible = !storedModel || storedModel === 'gemini-3.8-flash'
        return {
            messages: Array.isArray(parsed.messages) ? parsed.messages : [],
            sessionId: sessionCompatible && typeof parsed.sessionId === 'string' ? parsed.sessionId : null,
            useAccountBranding: parsed.useAccountBranding !== false,
            lastAiSnapshot:
                sessionCompatible && typeof parsed.lastAiSnapshot === 'string' ? parsed.lastAiSnapshot : null
        }
    } catch {
        return null
    }
}

const writePersistedState = (storageId, isLandingPage, state) => {
    try {
        sessionStorage.setItem(
            storageKey(storageId, isLandingPage),
            JSON.stringify({
                messages: state.messages || [],
                sessionId: state.sessionId || null,
                useAccountBranding: state.useAccountBranding !== false,
                lastAiSnapshot: state.lastAiSnapshot || null
            })
        )
    } catch {
        // Ignore quota / private-mode failures.
    }
}

const stableSimpleJson = (simple) => {
    try {
        return JSON.stringify(simple)
    } catch {
        return ''
    }
}

const AiRefine = ({
    templateId,
    landingPageId,
    isLandingPage = false,
    templateJson,
    updateElementsData,
    bodyTemplate,
    layouts = [],
    blocks = [],
    components = []
}) => {
    const siteMaster = useSelector((state) => state.siteMaster)
    const accountMaster = useSelector((state) => state.accountMaster)
    const storageId = isLandingPage ? landingPageId || templateId : templateId
    const refineEndpoint = isLandingPage ? '/ai/landing/refine' : '/ai/template/refine'
    const initialRef = useRef(null)
    if (!initialRef.current) {
        initialRef.current = readPersistedState(storageId, isLandingPage) || {
            messages: [],
            sessionId: null,
            useAccountBranding: false,
            lastAiSnapshot: null
        }
    }
    const [messages, setMessages] = useState(initialRef.current.messages)
    const [prompt, setPrompt] = useState('')
    const [useAccountBranding, setUseAccountBranding] = useState(initialRef.current.useAccountBranding)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    /** Server-held agent transcript handle — client never re-sends conversationMessages. */
    const [sessionId, setSessionId] = useState(initialRef.current.sessionId)
    /** Last AI-applied simple templateJson (from live canvas after apply); used to detect manual edits. */
    const [lastAiSnapshot, setLastAiSnapshot] = useState(initialRef.current.lastAiSnapshot)
    const pendingCanvasSnapshotRef = useRef(false)
    const messagesEndRef = useRef(null)

    // Persist across left-tool tab switches (AiRefine unmounts when leaving the AI panel).
    useEffect(() => {
        writePersistedState(storageId, isLandingPage, {
            messages,
            sessionId,
            useAccountBranding,
            lastAiSnapshot
        })
    }, [storageId, isLandingPage, messages, sessionId, useAccountBranding, lastAiSnapshot])

    useEffect(() => {
        if (messagesEndRef.current?.scrollIntoView) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages, sending])

    // Snapshot the canvas after an AI apply once parent state has updated (avoids stale dirty checks).
    useEffect(() => {
        if (!pendingCanvasSnapshotRef.current || !templateJson) return
        try {
            const simple = DragDropFunctions.convertToSimpleJson(templateJson)
            setLastAiSnapshot(stableSimpleJson(simple))
        } catch {
            // Keep prior snapshot if conversion fails.
        }
        pendingCanvasSnapshotRef.current = false
    }, [templateJson])

    const applyProposedTemplate = (proposedSimple) => {
        if (!proposedSimple || !bodyTemplate) {
            throw new Error(i18n.t('templates:add.drag.aiRefine.applyError'))
        }
        const full = DragDropFunctions.convertToFullJson(
            proposedSimple,
            cloneDeep(bodyTemplate),
            layouts,
            blocks,
            components
        )
        pendingCanvasSnapshotRef.current = true
        updateElementsData(full, '', true)
    }

    const sendMessage = async (rawPrompt) => {
        const trimmed = (rawPrompt || '').trim()
        if (!trimmed || sending) return

        if (!bodyTemplate) {
            setError(i18n.t('templates:add.drag.aiRefine.notReady'))
            return
        }

        const nextMessages = [...messages, { role: 'user', content: trimmed }]
        setMessages(nextMessages)
        setPrompt('')
        setError('')
        setSending(true)

        try {
            const simple = DragDropFunctions.convertToSimpleJson(templateJson)
            const hasSession = !!sessionId
            // While waiting for the post-apply canvas snapshot, treat as clean so we don't resync the pre-AI JSON.
            // No snapshot yet (reply-only prior turn / restore gap) ⇒ treat as dirty so we resync.
            const canvasDirty =
                hasSession &&
                !pendingCanvasSnapshotRef.current &&
                (!lastAiSnapshot || stableSimpleJson(simple) !== lastAiSnapshot)

            const payload = {
                prompt: trimmed,
                canvasDirty,
                useAccountBranding
            }

            if (hasSession) {
                payload.sessionId = sessionId
            }

            // First turn, or canvas diverged from the last AI apply — send full JSON.
            if (!hasSession || canvasDirty) {
                payload.templateJson = simple
            }

            const response = await axios.post(refineEndpoint, payload, { timeout: 120000 })

            const assistantMessages = Array.isArray(response.data?.messages)
                ? response.data.messages.filter((m) => m?.role === 'assistant' && m?.content)
                : []
            const fallbackSummary = response.data?.summary || i18n.t('templates:add.drag.aiRefine.updatedFallback')
            const reply =
                assistantMessages.length > 0 ? assistantMessages : [{ role: 'assistant', content: fallbackSummary }]

            // Apply canvas before committing sessionId — if apply fails, drop the
            // session so the next turn resyncs from the live editor instead of
            // refining a server snapshot that never made it onto the canvas.
            if (response.data?.templateJson) {
                applyProposedTemplate(response.data.templateJson)
            } else {
                // Reply-only: anchor dirty detection to the current canvas.
                setLastAiSnapshot(stableSimpleJson(simple))
            }

            setMessages([...nextMessages, ...reply])

            if (response.data?.sessionId) {
                setSessionId(response.data.sessionId)
            }
        } catch (err) {
            let message = i18n.t('templates:add.drag.aiRefine.genericError')
            if (err?.data?.error) {
                message = err.data.error
            } else if (err?.response?.data?.error) {
                message = err.response.data.error
            } else if (err?.message && err.message !== 'Network Error') {
                message = err.message
            }
            // Force a fresh start next turn (expired session, or apply failed after a successful refine).
            setSessionId(null)
            setLastAiSnapshot(null)
            pendingCanvasSnapshotRef.current = false
            setError(message)
            setMessages([
                ...nextMessages,
                {
                    role: 'assistant',
                    content: message
                }
            ])
        } finally {
            setSending(false)
        }
    }

    const hasAiTemplateBuilder = Boolean(accountMaster?.accountMaster?.beta?.aiTemplateBuilder)
    const aiEnabled = hasAiTemplateBuilder && (siteMaster?.siteId == 1 || siteMaster?.aiEnabled === true)

    if (!aiEnabled) {
        return (
            <div className='ai-refine'>
                <PanelHeader
                    hideBorder={false}
                    title={i18n.t('templates:add.drag.aiRefine.title')}
                    subtitle={i18n.t('templates:add.drag.aiRefine.unavailable')}
                />
            </div>
        )
    }

    return (
        <div className='ai-refine'>
            <PanelHeader
                hideBorder={false}
                title={i18n.t('templates:add.drag.aiRefine.title')}
                subtitle={i18n.t('templates:add.drag.aiRefine.subtitle')}
            />
            <div className='ai-refine__body'>
                {/* <Toggle
                    name='useAccountBranding'
                    outerClassName='mar-b15'
                    checked={useAccountBranding}
                    onChange={() => setUseAccountBranding(!useAccountBranding)}
                    after={i18n.t('templates:add.drag.aiRefine.useAccountBranding')}
                    disabled={sending}
                /> */}

                <div className='ai-refine__messages'>
                    {messages.length === 0 && !sending && (
                        <div className='ai-refine__empty'>
                            <Icons icon='sparkle2' color='#7950EA' style={{ width: 24, height: 24 }} />
                            <p>{i18n.t('templates:add.drag.aiRefine.emptyHint')}</p>
                            <div className='ai-refine__suggestions'>
                                {SUGGESTION_KEYS.map((key) => (
                                    <button
                                        key={key}
                                        type='button'
                                        className='ai-refine__suggestion'
                                        onClick={() => sendMessage(i18n.t(`templates:add.drag.aiRefine.${key}`))}
                                    >
                                        {i18n.t(`templates:add.drag.aiRefine.${key}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {messages.map((message, index) => (
                        <div
                            key={`${message.role}-${index}`}
                            className={`ai-refine__bubble ai-refine__bubble--${message.role}`}
                        >
                            {message.content}
                        </div>
                    ))}
                    {sending && (
                        <div className='ai-refine__loading'>
                            <AiLoadingIcon style={{ height: 80, width: '100%' }} />
                            <p>{i18n.t('templates:add.drag.aiRefine.generating')}</p>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {error && !sending && <div className='ai-refine__error'>{error}</div>}

                <div className='ai-refine__composer'>
                    <TextArea
                        name='aiRefinePrompt'
                        className='txtInput'
                        placeholder={i18n.t('templates:add.drag.aiRefine.promptPlaceholder')}
                        value={prompt}
                        onChange={(ev) => {
                            setPrompt(ev.target.value)
                            setError('')
                        }}
                        rows={3}
                        disabled={sending}
                    />
                    <Button
                        className='button-primary mar-t10'
                        text={i18n.t('templates:add.drag.aiRefine.sendButton')}
                        iconRight={<Icons icon='arrowRight' color='#FFFFFF' />}
                        onClick={() => sendMessage(prompt)}
                        disabled={sending || !prompt.trim()}
                        postHogTag='ai-template-refine'
                    />
                </div>
            </div>
        </div>
    )
}

export default AiRefine
